import { useEffect, useRef, useState } from "react";
import type { ExportFormatStructureNode, ImportSource } from "@capture/shared";
import { FormatStructureNode } from "./FormatStructureNode.js";
import { SlotPropertiesPanel } from "./SlotPropertiesPanel.js";
import { SchemaImportModal, type SchemaImportResult } from "./SchemaImportModal.js";
import styles from "../../form-config/export/export-composer.module.css";

const ROOT = "__root__";

/** Walk `nodes` along an index path; return the node or null. */
function nodeAt(nodes: ExportFormatStructureNode[], path: number[]): ExportFormatStructureNode | null {
  let list = nodes;
  let found: ExportFormatStructureNode | null = null;
  for (const idx of path) {
    found = list[idx] ?? null;
    if (!found) return null;
    list = found.children ?? [];
  }
  return found;
}

/** Total node count across the whole tree (containers + fields). */
function countNodes(nodes: ExportFormatStructureNode[]): number {
  return nodes.reduce((n, node) => n + 1 + countNodes(node.children ?? []), 0);
}

/** Return a copy of `nodes` with the node at `path` replaced by `next`. */
function replaceAt(
  nodes: ExportFormatStructureNode[],
  path: number[],
  next: ExportFormatStructureNode,
): ExportFormatStructureNode[] {
  if (path.length === 0) return nodes;
  const [head, ...rest] = path;
  return nodes.map((n, i) => {
    if (i !== head) return n;
    if (rest.length === 0) return next;
    return { ...n, children: replaceAt(n.children ?? [], rest, next) };
  });
}

export function FormatStructureEditor({
  value,
  onChange,
  format,
  onImport,
}: {
  value: ExportFormatStructureNode[];
  onChange: (next: ExportFormatStructureNode[]) => void;
  format: "json" | "xml";
  /** Called with the full SchemaImportResult (columns, contract, structure,
   *  rawSchemaText) when the user imports a schema — mirrors
   *  FormatColumnsEditor.onImport. */
  onImport?: (result: SchemaImportResult) => void;
}) {
  const [selectedPath, setSelectedPath] = useState<number[] | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const root: ExportFormatStructureNode = { name: ROOT, type: "group", children: value };
  const selected = selectedPath ? nodeAt(value, selectedPath) : null;

  // A delete shifts sibling indices, so a held index-path can resolve to the
  // WRONG node (not just null) after a delete — which would silently bind the
  // properties panel to a different slot. Detect any structural shrink (only a
  // delete reduces the total node count; panel edits preserve it) and drop the
  // selection so the panel can't edit a node the user didn't pick.
  const total = countNodes(value);
  const prevTotal = useRef(total);
  useEffect(() => {
    if (total < prevTotal.current) setSelectedPath(null);
    prevTotal.current = total;
  }, [total]);

  // Schema source that best fits the format being authored.
  const initialSource: ImportSource = format === "json" ? "json_schema" : "xsd";

  return (
    <div>
      <div className={styles.structureToolbar}>
        <button
          type="button"
          className="rb-btn"
          onClick={() => setImportOpen(true)}
        >
          Import from schema
        </button>
      </div>

      {/* An import seeds (replaces) the tree — root-level merge has no obvious
          semantics, so we always replace. hasExistingColumns=false suppresses
          the modal's flat replace/append prompt so Apply is one click. */}
      <SchemaImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        hasExistingColumns={false}
        initialSource={initialSource}
        applyTarget="structure"
        onApply={(result) => {
          onChange(result.structure);
          onImport?.(result);
          setSelectedPath(null);
          setImportOpen(false);
        }}
      />

      <div className={styles.structureTabPanes}>
        <div className={styles.structureLeftPane}>
          <FormatStructureNode
            node={root}
            depth={0}
            path={[]}
            selectedPath={selectedPath}
            onSelect={(p) => setSelectedPath(p.length ? p : null)}
            onUpdate={(updated) => onChange(updated.children ?? [])}
            onDelete={() => onChange([])}
            format={format}
          />
        </div>
        <SlotPropertiesPanel
          node={selected}
          onChange={(next) => {
            if (!selectedPath) return;
            onChange(replaceAt(value, selectedPath, next));
          }}
        />
      </div>
    </div>
  );
}
